import React, { useState } from 'react';
import { IDriver } from '../../models/Driver.model';
import yup from '../../common/validation';
import { Form, Formik } from 'formik';
import { Button, Modal } from 'react-bootstrap';
import TextField from '../shared/formikFields/TextField';
import { Spinner2 } from '../shared/spinner2';
import { Form as BsForm, Row, Col } from 'react-bootstrap';
import { debounceEventHandler } from '../shared/submitFormUtility';
import ToggleButton from '../shared/customInputTypes/ToggleButton';
import DriverService from '../../services/DriversService';
import { Toastr } from '../../utils/Toastr';
import { locale } from '../../common/localization/localizationService';

interface IDriverModalProps {
  driver: IDriver;
  show: boolean;
  onHide: (rerender: boolean) => void;
  customerId: number;
  contractorId: number;
  existing: Array<string>;
}

export default function DriverModal(props: IDriverModalProps): JSX.Element {
  const isNew = !props.driver;
  const { show, onHide } = props;

  const [isLoading, setLoading] = useState(false);

  const newDriver = () => {
    const newDriver: IDriver = {
      active: true,
      contractorId: props.contractorId,
      customerId: props.customerId,
      driver: '',
      firstName: '',
      lastName: '',
      password: null,
      phone: '',
    };
    return newDriver;
  };

  const driver = isNew ? newDriver() : props.driver;

  const getSchema = () =>
    yup.object({
      driver: isNew
        ? yup
            .string()
            .required()
            .test(
              'is-unique',
              locale.drivers._alreadyExist,
              (driver: string) =>
                !props.existing ||
                !driver ||
                !props.existing.includes(driver.trim()),
            )
            .label(locale.drivers._driver)
        : yup.string().required().label(locale.drivers._driver),
    });

  const onSubmit = async (driver: IDriver) => {
    if (isNew) {
      try {
        setLoading(true);
        await DriverService.createNewDriver(driver);
        Toastr.success(locale.drivers._created);
        setLoading(false);
        onHide(true);
      } catch (err) {
        Toastr.error(err);
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        await DriverService.updateDriver(driver);
        Toastr.success(locale.drivers._updated);
        setLoading(false);
        onHide(true);
      } catch (err) {
        Toastr.error(err);
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Formik
        onSubmit={onSubmit}
        initialValues={driver}
        validationSchema={getSchema()}
      >
        {({
          handleSubmit,
          values,
          isSubmitting,
          isValid,
          dirty,
          setFieldValue,
        }) => (
          <Modal show={show} onHide={() => onHide(false)}>
            <Spinner2 show={isLoading} />
            <Modal.Header closeButton>
              <Modal.Title>
                {isNew
                  ? locale.drivers._createNew
                  : `${locale.drivers._update} - ${driver.driver}`}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form noValidate onSubmit={handleSubmit}>
                <TextField
                  label={locale.drivers._driver}
                  name="driver"
                  placeholder={locale.drivers._driver}
                  disabled={!isNew}
                />
                <TextField
                  label={locale.drivers._firstName}
                  name="firstName"
                  placeholder={locale.drivers._firstName}
                />
                <TextField
                  label={locale.drivers._lastName}
                  name="lastName"
                  placeholder={locale.drivers._lastName}
                />
                <TextField
                  label={locale.drivers._phoneNr}
                  name="phone"
                  placeholder={locale.drivers._phoneNr}
                />
                <TextField
                  label={locale.drivers._password}
                  name="password"
                  placeholder={locale.drivers._password}
                />
                <BsForm.Group
                  as={Row}
                  className="checkbox-row mb-3 route-active-input"
                >
                  <BsForm.Label column sm={6}>
                    {locale.drivers._active}
                  </BsForm.Label>
                  <Col sm={6} className="checkbox-wrapper">
                    <ToggleButton
                      onToggle={(e) => {
                        setFieldValue('active', e.currentTarget.checked);
                      }}
                      value={values.active}
                    />
                  </Col>
                </BsForm.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => onHide(false)}>
                {locale.drivers._cancel}
              </Button>
              <Button
                type="submit"
                variant="primary"
                onClick={debounceEventHandler(handleSubmit, 250)}
                disabled={
                  !values.driver?.trim() ||
                  isSubmitting ||
                  !isValid ||
                  (!isNew && !dirty)
                }
              >
                {locale.drivers._save}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    </>
  );
}
